import React from "react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";

const benefits = [
  {
    icon: "🚀",
    title: "Generate More Leads",
    points: [
      "Professional website attracts the right audience",
      "Targeted campaigns run 24/7 on autopilot",
      "Visitors converted into paying customers",
      "SEO & ads bring consistent inbound traffic",
    ],
    stat: "3x",
    statLabel: "More Inquiries",
    gradient: "from-blue-500 to-sky-400",
    bg: "from-blue-50 to-sky-50",
    border: "border-blue-200",
    tag: "bg-blue-100 text-blue-700",
    dot: "bg-blue-400",
  },
  {
    icon: "💰",
    title: "Boost Sales & Revenue",
    points: [
      "E-commerce & booking systems built to convert",
      "Remove friction from your sales funnel",
      "High-ROAS ad campaigns that scale",
      "Close more deals with less manual effort",
    ],
    stat: "4.8x",
    statLabel: "Avg. ROAS",
    gradient: "from-orange-400 to-pink-400",
    bg: "from-orange-50 to-pink-50",
    border: "border-orange-200",
    tag: "bg-orange-100 text-orange-700",
    dot: "bg-orange-400",
  },

  {
    icon: "👥",
    title: "Reduce Team Burden",
    points: [
      "Task & attendance management in one app",
      "Cut down back-and-forth communication",
      "Role-based access for every team member",
      "Team works smarter, not harder",
    ],
    stat: "50%",
    statLabel: "Less Manual Work",
    gradient: "from-teal-400 to-green-400",
    bg: "from-teal-50 to-green-50",
    border: "border-teal-200",
    tag: "bg-teal-100 text-teal-700",
    dot: "bg-teal-400",
  },
  {
    icon: "🌐",
    title: "Build Online Authenticity",
    points: [
      "Professional website builds instant trust",
      "Strong social media presence & branding",
      "Consistent brand identity across all platforms",
      "Customers find & trust you before calling",
    ],
    stat: "2x",
    statLabel: "Brand Trust",
    gradient: "from-indigo-400 to-blue-400",
    bg: "from-indigo-50 to-blue-50",
    border: "border-indigo-200",
    tag: "bg-indigo-100 text-indigo-700",
    dot: "bg-indigo-400",
  },
 
];

export default function Benefits() {
  const ref = useScrollAnimation();

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Fraunces:wght@700;900&display=swap');
        .ben-heading { font-family: 'Fraunces', serif; }
        .ben-body    { font-family: 'Plus Jakarta Sans', sans-serif; }
      `}</style>

      <section
        id="benefits"
        className="ben-body py-24 relative overflow-hidden bg-gradient-to-b from-blue-50 to-white"
      >
        {/* Blobs */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-100/50 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-sky-100/60 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-5 md:px-10 relative">

          {/* ── Top: Left label + Right heading ── */}
          <div ref={ref} className="section-animate flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">

            {/* Left */}
            <div className="flex flex-col gap-3 md:max-w-xs">
              <span className="inline-block w-fit text-xs font-bold text-blue-600 tracking-widest uppercase bg-blue-100 border border-blue-200 rounded-full px-5 py-1.5">
                Why It Matters
              </span>
              <p className="text-slate-500 text-sm font-medium leading-relaxed">
                Every service we deliver is built around one goal — making your business more efficient, more visible, and more profitable.
              </p>
              <div className="w-12 h-1 rounded-full bg-gradient-to-r from-blue-500 to-sky-400 mt-1" />
            </div>

            {/* Right — heading */}
            <div className="md:text-right md:max-w-xl">
              <h2 className="ben-heading font-black text-4xl md:text-5xl lg:text-6xl text-black leading-tight">
                How We Help Your{" "}
                <span
                  style={{
                    background: "linear-gradient(135deg,#1d4ed8 0%,#0ea5e9 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Business Grow
                </span>
              </h2>
            </div>
          </div>

          {/* ── Cards grid ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <div
                key={i}
                className={`group relative bg-white border ${b.border} rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:shadow-blue-100/60 transition-all duration-300 hover:-translate-y-1`}
              >
                {/* Top gradient bar */}
                <div className={`h-1 w-full bg-gradient-to-r ${b.gradient}`} />

                <div className={`p-6 bg-gradient-to-b ${b.bg} h-full flex flex-col gap-4`}>

                  {/* Icon + Stat row */}
                  <div className="flex items-start justify-between">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${b.gradient} flex items-center justify-center text-2xl shadow-md`}>
                      {b.icon}
                    </div>
                    <div className="flex flex-col items-end">
                      <span className={`text-2xl font-black ben-heading ${b.tag.split(" ")[1]}`}>
                        {b.stat}
                      </span>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                        {b.statLabel}
                      </span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="ben-heading font-bold text-lg text-black leading-snug">
                    {b.title}
                  </h3>

                  {/* ── Bullet points ── */}
                  <ul className="flex flex-col gap-2 flex-1">
                    {b.points.map((point, j) => (
                      <li key={j} className="flex items-start gap-2.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${b.dot} flex-shrink-0 mt-1.5`} />
                        <span className="text-slate-600 text-sm font-medium leading-snug">
                          {point}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Bottom tag */}
                  <div className="pt-3 border-t border-slate-100">
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${b.tag}`}>
                      For Your Business
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Bottom CTA ── */}
          <div className="mt-16 text-center">
            <p className="text-slate-400 text-sm font-medium mb-4">
              Ready to take your business to the next level?
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-200"
            >
              Get Free Consultation →
            </a>
          </div>
        </div>
      </section>
    </>
  );
}